import { useState, useMemo } from 'react';
import { MapPin, ChevronDown, Check, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCity } from '@/contexts/CityContext';

export function CitySwitcher() {
  const { city, setCity, isDetecting, cities, isLoadingCities } = useCity();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cities;
    return cities.filter((c) => c.city.toLowerCase().includes(q));
  }, [cities, query]);

  const showSearch = cities.length > 8;

  return (
    <DropdownMenu onOpenChange={(open) => !open && setQuery('')}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-sm"
          aria-label={`Current city: ${city}. Click to change.`}
        >
          <MapPin className="w-4 h-4" aria-hidden="true" />
          {isDetecting ? 'Detecting...' : city}
          <ChevronDown className="w-3 h-3 opacity-60" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60 max-h-[70vh] overflow-y-auto">
        {showSearch && (
          <div className="p-2 sticky top-0 bg-popover z-10">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 opacity-50" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="Search cities"
                className="h-8 pl-7 text-sm"
              />
            </div>
          </div>
        )}

        {isLoadingCities && (
          <div className="px-3 py-2 text-sm text-muted-foreground">Loading cities…</div>
        )}

        {!isLoadingCities && filtered.length === 0 && (
          <div className="px-3 py-2 text-sm text-muted-foreground">No cities found</div>
        )}

        {filtered.map((c) => (
          <DropdownMenuItem
            key={c.city}
            onClick={() => setCity(c.city)}
            className={`min-h-11 ${c.city === city ? 'bg-muted font-medium' : ''}`}
          >
            <MapPin className="w-4 h-4 mr-2 shrink-0" />
            <span className="flex-1 truncate">{c.city}</span>
            <span className="ml-2 text-xs text-muted-foreground tabular-nums">
              {c.listingCount.toLocaleString()}
            </span>
            {c.city === city && <Check className="w-3.5 h-3.5 ml-2 shrink-0" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
